export function getAudioContextCtor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ||
    ((window as unknown as Record<string, unknown>).webkitAudioContext as
      typeof AudioContext | undefined)
  );
}

/**
 * Plays a short 440 Hz sine wave (gentle fade in/out) so the user can
 * verify their speakers or headphones are audible before joining the call.
 * Resolves when the tone finishes; rejects if no AudioContext is available.
 *
 * MUST be invoked from a user-gesture handler. Browsers (notably iOS
 * Safari/Chrome and stock desktop Chrome under the autoplay policy) create
 * `AudioContext` in `suspended` state when there is no transient user
 * activation, and an oscillator scheduled against a suspended context's
 * clock plays no audible sound — but `osc.onended` still fires after the
 * scheduled duration, so callers think playback succeeded. We therefore
 * always `await ctx.resume()` before scheduling, which is permitted as
 * long as we're inside the gesture's activation window.
 */
export async function playTestTone(): Promise<void> {
  const ACtor = getAudioContextCtor();
  if (!ACtor) {
    throw new Error('AudioContext not supported');
  }
  const ctx = new ACtor();

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      // Some browsers reject `resume()` if the gesture is too stale.
      // Fall through and let the schedule happen anyway: in the worst
      // case the tone is silent and the user simply taps "Play again".
    }
  }

  return new Promise((resolve, reject) => {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 440;
      const t = ctx.currentTime;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.05);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.8);
      gain.gain.linearRampToValueAtTime(0, t + 0.9);
      osc.start(t);
      osc.stop(t + 0.9);
      osc.onended = () => {
        ctx.close().catch(() => {});
        resolve();
      };
    } catch (err) {
      ctx.close().catch(() => {});
      reject(err);
    }
  });
}
